import type { Appointment, Daycare, Dog, MembershipPurchase, Payment, PaymentCreate, Price, Service, Stay } from '../../../../../packages/contracts/src/index.js';
import { buildPriceLookups, dogIdsForSource, hoursBetween, nightsBetween, quantityForUnit, serviceIdsByDogForSource, serviceIdsForSource, servicePricesForSource, uniqueServiceIdsForSource } from '../../shared/billing';
import { isOperationalRatePrice } from '../../shared/services';

export type SourceType = 'daycare' | 'stay' | 'service' | 'membership' | 'other';

export interface CartItem {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly quantity: number;
  readonly amountCents: number;
  readonly sourceType: SourceType;
  readonly revenueType?: 'daycare' | 'stay' | 'service' | 'membership' | 'other';
  readonly sourceId: string;
  readonly customerId?: string;
  readonly groupId: string;
  readonly groupLabel: string;
  readonly parentId: string;
  readonly itemKind: 'main' | 'dog' | 'service' | 'extra';
  readonly dogId?: string;
  readonly paidCents?: number;
  readonly appointmentId?: string;
  readonly serviceId?: string;
  readonly relatedStayId?: string;
  readonly relatedDaycareId?: string;
}

export interface SourceOption {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly sourceType: SourceType;
  readonly source: Appointment | Daycare | MembershipPurchase | Service | Stay;
}

export interface SuggestedItem {
  readonly label: string;
  readonly description?: string;
  readonly quantity: number;
  readonly amountCents: number;
  readonly sourceType: SourceType;
  readonly revenueType?: 'daycare' | 'stay' | 'service' | 'membership' | 'other';
  readonly sourceId: string;
  readonly customerId?: string;
  readonly groupId: string;
  readonly groupLabel: string;
  readonly parentId: string;
  readonly itemKind: 'main' | 'dog' | 'service' | 'extra';
  readonly dogId?: string;
  readonly paidCents?: number;
  readonly appointmentId?: string;
  readonly serviceId?: string;
  readonly relatedStayId?: string;
  readonly relatedDaycareId?: string;
}

export interface CartGroup {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly items: CartItem[];
}

export interface StoredCart {
  readonly expiresAt: number;
  readonly items: CartItem[];
}

export interface CartPaymentSummary {
  readonly billBuckets: ReturnType<typeof paymentBuckets>;
  readonly totalCents: number;
  readonly paidCents: number;
  readonly remainingCents: number;
}

export interface SuggestedSelectionDefaults {
  readonly quantity: string;
  readonly amount: string;
}

export interface PaymentDraftInput {
  readonly customerId: string;
  readonly items: CartItem[];
  readonly payments: Payment[];
  readonly paymentCents: number;
  readonly method: PaymentCreate['method'];
  readonly notes?: string;
}

export interface CanSubmitPaymentInput {
  readonly customerId: string;
  readonly itemCount: number;
  readonly isPending: boolean;
  readonly paymentCents: number;
  readonly remainingCents: number;
}

export interface CartItemsForSourceInput {
  readonly selectedSource: SourceOption;
  readonly suggestedItems: SuggestedItem[];
  readonly existingItems: CartItem[];
  readonly sourceCustomerId: string;
  readonly sourceTypeLabel: string;
  readonly manualAmountCents: number;
  readonly quantity: number;
  readonly isPaid?: boolean;
  readonly createId: () => string;
}

export interface BuildSourceOptionsInput {
  readonly sourceType: SourceType | '';
  readonly customerId: string;
  readonly memberships: MembershipPurchase[];
  readonly services: Service[];
  readonly stays: Stay[];
  readonly daycares: Daycare[];
  readonly linkedAppointment?: Appointment;
  readonly linkedService?: Service;
  readonly linkedStay?: Stay;
  readonly linkedDaycare?: Daycare;
  readonly dogs: Dog[];
  readonly pricesByServiceId: Record<string, Price>;
  readonly priceLookups: ReturnType<typeof buildPriceLookups>;
  readonly payments: Payment[];
  readonly appointments: Appointment[];
  readonly isOperationalRateService: (service: Service) => boolean;
  readonly formatCurrency: (cents: number) => string;
  readonly formatDate: (value: string) => string;
  readonly formatDateTime: (value: string) => string;
}

export interface SelectedPriceContextInput {
  readonly sourceType: SourceType | '';
  readonly sourceId: string;
  readonly stays: Stay[];
  readonly daycares: Daycare[];
  readonly linkedStay?: Stay;
  readonly linkedDaycare?: Daycare;
  readonly linkedAppointment?: Appointment;
  readonly today: string;
}

export interface PriceServiceIdsInput {
  readonly sourceType: SourceType | '';
  readonly sourceId: string;
  readonly stays: Stay[];
  readonly daycares: Daycare[];
  readonly linkedStay?: Stay;
  readonly linkedDaycare?: Daycare;
  readonly linkedAppointment?: Appointment;
}

export function cartForDirectPayment(storedCart: StoredCart, directSourceId: string): StoredCart {
  return directSourceId ? { expiresAt: 0, items: [] } : storedCart;
}

export function isStoredCartItem(value: unknown): value is CartItem {
  if (!value || typeof value !== 'object') return false;
  const item = value as Partial<CartItem>;
  return (
    typeof item.id === 'string' &&
    typeof item.label === 'string' &&
    typeof item.quantity === 'number' &&
    typeof item.amountCents === 'number' &&
    (item.sourceType === 'daycare' || item.sourceType === 'stay' || item.sourceType === 'service' || item.sourceType === 'membership' || item.sourceType === 'other') &&
    typeof item.sourceId === 'string' &&
    typeof item.groupId === 'string' &&
    typeof item.groupLabel === 'string' &&
    typeof item.parentId === 'string' &&
    (item.itemKind === 'main' || item.itemKind === 'dog' || item.itemKind === 'service' || item.itemKind === 'extra')
  );
}

export function selectedPriceDateForSource(input: SelectedPriceContextInput) {
  if (input.sourceType === 'stay') {
    const stay = input.linkedStay ?? input.stays.find((item) => item.id === input.sourceId);
    return stay?.checkInDate ?? input.today;
  }
  if (input.sourceType === 'daycare') {
    const daycare = input.linkedDaycare ?? input.daycares.find((item) => item.id === input.sourceId);
    return daycare?.startAt.slice(0, 10) ?? input.today;
  }
  if (input.sourceType === 'service' && input.linkedAppointment) return input.linkedAppointment.scheduledAt.slice(0, 10);
  return input.today;
}

export function priceServiceIdsForSource(input: PriceServiceIdsInput) {
  const ids = new Set<string>();
  if (input.sourceType === 'service' && input.sourceId) {
    if (input.linkedAppointment) input.linkedAppointment.serviceIds.forEach((serviceId) => ids.add(serviceId));
    else if (!input.sourceId.startsWith('appt_')) ids.add(input.sourceId);
  }
  if (input.sourceType === 'stay' && input.sourceId) {
    const stay = input.linkedStay ?? input.stays.find((item) => item.id === input.sourceId);
    if (stay) uniqueServiceIdsForSource(stay).forEach((serviceId) => ids.add(serviceId));
  }
  if (input.sourceType === 'daycare' && input.sourceId) {
    const daycare = input.linkedDaycare ?? input.daycares.find((item) => item.id === input.sourceId);
    if (daycare) uniqueServiceIdsForSource(daycare).forEach((serviceId) => ids.add(serviceId));
  }
  return [...ids].sort();
}

export function buildSourceOptions(input: BuildSourceOptionsInput): SourceOption[] {
  if (!input.sourceType || input.sourceType === 'other') return [];
  const sourceType = input.sourceType;
  if (sourceType === 'membership') {
    return input.memberships
      .filter((membership) => !input.customerId || membership.customerId === input.customerId)
      .map((membership) => {
        const suggested = suggestedItemsForMembership(membership);
        const paidCents = paidCentsForSource(input.payments, 'membership', membership.id);
        const dueCents = totalWithFrozenFloor(input.payments, 'membership', membership.id, suggested);
        const billReference = billReferenceForSource(input.payments, 'membership', membership.id);
        return {
          id: membership.id,
          label: `${billReference} - ${membership.packageName} - vence ${membership.expiresAt} - pagado ${input.formatCurrency(paidCents)} de ${input.formatCurrency(dueCents)}`,
          sourceType,
          source: membership,
        };
      });
  }
  if (sourceType === 'service') {
    if (input.linkedAppointment) {
      return [{
        id: input.linkedAppointment.id,
        label: input.linkedAppointment.serviceIds.map((serviceId) => input.services.find((service) => service.id === serviceId)?.name ?? serviceId).join(', '),
        sourceType,
        source: input.linkedAppointment,
      }];
    }
    const serviceOptions = input.services.filter((service) => !input.isOperationalRateService(service));
    const withLinked = input.linkedService && !serviceOptions.some((service) => service.id === input.linkedService?.id)
      ? [input.linkedService, ...serviceOptions]
      : serviceOptions;
    return withLinked.map((service) => ({
      id: service.id,
      label: service.name,
      sourceType,
      source: service,
    }));
  }
  if (sourceType === 'stay') {
    const stayOptions = input.linkedStay && !input.stays.some((stay) => stay.id === input.linkedStay?.id)
      ? [input.linkedStay, ...input.stays]
      : input.stays;
    return stayOptions
      .filter((stay) => !input.customerId || stay.customerId === input.customerId)
      .filter((stay) => stay.status !== 'checked-out' && stay.status !== 'cancelled')
      .map((stay) => {
        const dogNames = dogNamesForSource(stay, input.dogs);
        const suggested = suggestedItemsForSource({ id: stay.id, label: '', sourceType: 'stay', source: stay }, input.services, input.dogs, input.pricesByServiceId, input.priceLookups, input.payments, input.appointments);
        const paidCents = directlyPaidCentsForSource(input.payments, 'stay', stay.id) + paidCentsForSuggestedItems(suggested);
        const dueCents = totalWithFrozenFloor(input.payments, 'stay', stay.id, suggested);
        const billReference = billReferenceForSource(input.payments, 'stay', stay.id);
        return {
          id: stay.id,
          label: `${billReference} - ${dogNames} - ${input.formatDate(stay.checkInDate)} a ${input.formatDate(stay.checkOutDate)} - pagado ${input.formatCurrency(paidCents)} de ${input.formatCurrency(dueCents)}`,
          sourceType,
          source: stay,
        };
      });
  }

  const daycareOptions = input.linkedDaycare && !input.daycares.some((daycare) => daycare.id === input.linkedDaycare?.id)
    ? [input.linkedDaycare, ...input.daycares]
    : input.daycares;
  return daycareOptions
    .filter((daycare) => !daycare.prepaidByMembership)
    .filter((daycare) => !input.customerId || daycare.customerId === input.customerId)
    .filter((daycare) => daycare.status !== 'checked-out' && daycare.status !== 'cancelled')
    .map((daycare) => {
      const dogNames = dogNamesForSource(daycare, input.dogs);
      const suggested = suggestedItemsForSource({ id: daycare.id, label: '', sourceType: 'daycare', source: daycare }, input.services, input.dogs, input.pricesByServiceId, input.priceLookups, input.payments, input.appointments);
      const paidCents = directlyPaidCentsForSource(input.payments, 'daycare', daycare.id) + paidCentsForSuggestedItems(suggested);
      const dueCents = totalWithFrozenFloor(input.payments, 'daycare', daycare.id, suggested);
      const billReference = billReferenceForSource(input.payments, 'daycare', daycare.id);
      return {
        id: daycare.id,
        label: `${billReference} - ${dogNames} - ${input.formatDateTime(daycare.startAt)} - pagado ${input.formatCurrency(paidCents)} de ${input.formatCurrency(dueCents)}`,
        sourceType,
        source: daycare,
      };
    });
}

export function cartItemsForSource(input: CartItemsForSourceInput): CartItem[] {
  if (input.existingItems.some((item) => item.customerId && item.customerId !== input.sourceCustomerId)) throw new Error('El carrito solo puede contener cobros de un cliente.');
  if (input.isPaid) throw new Error('Este concepto ya esta pagado y no puede agregarse al carrito.');
  if (
    (input.selectedSource.sourceType === 'stay' || input.selectedSource.sourceType === 'daycare' || input.selectedSource.sourceType === 'membership') &&
    input.existingItems.some((item) => item.parentId === input.selectedSource.id)
  ) {
    throw new Error('Este concepto ya está en el carrito.');
  }

  const quantity = Math.max(1, input.quantity);
  const amountCents = Math.max(0, input.manualAmountCents);
  if (input.suggestedItems.length > 0) {
    return input.suggestedItems.map((item, index) => ({
      id: input.createId(),
      label: item.label,
      description: item.description ?? defaultPaymentDescription(item.label),
      quantity: index === 0 ? quantity : item.quantity,
      amountCents: index === 0 ? amountCents : item.amountCents,
      sourceType: item.sourceType,
      revenueType: item.revenueType,
      sourceId: item.sourceId,
      customerId: item.customerId ?? input.sourceCustomerId,
      groupId: item.groupId,
      groupLabel: item.groupLabel,
      parentId: item.parentId,
      itemKind: item.itemKind,
      dogId: item.dogId,
      paidCents: item.paidCents,
      appointmentId: item.appointmentId,
      serviceId: item.serviceId,
      relatedStayId: item.relatedStayId,
      relatedDaycareId: item.relatedDaycareId,
    }));
  }

  return [{
    id: input.createId(),
    label: `${input.sourceTypeLabel} - ${input.selectedSource.label}`,
    description: ('description' in input.selectedSource.source && input.selectedSource.source.description?.trim()) || defaultPaymentDescription(input.selectedSource.label),
    quantity,
    amountCents,
    sourceType: input.selectedSource.sourceType,
    revenueType: input.selectedSource.sourceType,
    sourceId: input.selectedSource.id,
    customerId: input.sourceCustomerId,
    groupId: input.selectedSource.id,
    groupLabel: input.selectedSource.label,
    parentId: input.selectedSource.id,
    itemKind: 'main',
  }];
}

export function createPaymentDraft(input: PaymentDraftInput): PaymentCreate {
  const paymentCents = Math.max(0, input.paymentCents);
  const remainingCartCents = paymentBuckets(input.items, input.payments).reduce((total, bucket) => total + bucket.remainingCents, 0);
  if (!input.customerId) throw new Error('Selecciona un cliente antes de registrar el pago.');
  if (input.items.some((item) => item.customerId && item.customerId !== input.customerId)) throw new Error('El carrito solo puede contener cobros de un cliente.');
  if (input.items.some((item) => item.sourceType === 'other' && (!Number.isSafeInteger(item.quantity) || item.quantity <= 0 || !Number.isSafeInteger(item.amountCents * item.quantity)))) throw new Error('Cantidad o importe inválido.');
  if (input.items.length === 0) throw new Error('Agrega al menos un concepto al carrito.');
  if (paymentCents <= 0) throw new Error('El monto a pagar debe ser mayor a cero.');
  if (paymentCents > remainingCartCents) throw new Error('El monto no puede exceder el saldo pendiente del carrito.');

  const paymentItems = buildPaymentLineItems(input.items, input.payments, paymentCents);
  if (paymentItems.length === 0) throw new Error('No hay monto pendiente para registrar.');

  return {
    customerId: input.customerId,
    sourceType: input.items[0]?.sourceType,
    sourceId: input.items[0]?.sourceId,
    billReference: paymentItems[0]?.billReference,
    billTotalCents: paymentItems[0]?.billTotalCents,
    items: paymentItems,
    method: input.method,
    notes: input.notes || undefined,
  };
}

export function canSubmitPayment(input: CanSubmitPaymentInput) {
  return Boolean(input.customerId) &&
    input.itemCount > 0 &&
    !input.isPending &&
    input.paymentCents > 0 &&
    input.paymentCents <= input.remainingCents;
}

export function centsFromAmountInput(value: string) {
  return Math.max(0, Math.round(Number(value || '0') * 100));
}

export function quantityFromInput(value: string | number) {
  const quantity = Number(value || '1');
  return Number.isFinite(quantity) ? Math.max(1, quantity) : 1;
}

export function cartPaymentSummary(items: CartItem[], payments: Payment[]): CartPaymentSummary {
  const billBuckets = paymentBuckets(items, payments);
  return {
    billBuckets,
    totalCents: billBuckets.reduce((total, bucket) => total + bucket.dueCents, 0),
    paidCents: billBuckets.reduce((total, bucket) => total + bucket.paidCents, 0),
    remainingCents: billBuckets.reduce((total, bucket) => total + bucket.remainingCents, 0),
  };
}

export function suggestedSelectionDefaults(items: SuggestedItem[]): SuggestedSelectionDefaults | undefined {
  const firstItem = items[0];
  if (!firstItem) return undefined;
  return {
    quantity: String(firstItem.quantity),
    amount: String(firstItem.amountCents / 100),
  };
}

export function paymentStateForSource(source: SourceOption, suggestedItems: SuggestedItem[], payments: Payment[]) {
  const dueCents = source.sourceType === 'service'
    ? totalForSuggestedItems(suggestedItems)
    : totalWithFrozenFloor(payments, source.sourceType, source.id, suggestedItems);
  const paidCents = source.sourceType === 'service'
    ? paidCentsForSuggestedItems(suggestedItems)
    : directlyPaidCentsForSource(payments, source.sourceType, source.id) + paidCentsForSuggestedItems(suggestedItems);
  return {
    dueCents,
    isPaid: dueCents > 0 && paidCents >= dueCents,
    paidCents,
    remainingCents: Math.max(0, dueCents - paidCents),
  };
}

export function suggestedItemsForSource(
  sourceOption: SourceOption,
  services: Service[],
  dogs: Dog[],
  pricesByServiceId: Record<string, Price>,
  priceLookups: ReturnType<typeof buildPriceLookups>,
  payments: Payment[] = [],
  appointments: Appointment[] = [],
): SuggestedItem[] {
  void priceLookups;
  if (sourceOption.sourceType === 'other') return [];
  if (sourceOption.sourceType === 'membership') {
    return suggestedItemsForMembership(sourceOption.source as MembershipPurchase);
  }
  if (sourceOption.sourceType === 'service') {
    const appointment = isAppointment(sourceOption.source) ? sourceOption.source : undefined;
    const serviceIds = appointment ? appointment.serviceIds : [(sourceOption.source as Service).id];
    const groupLabel = appointment ? `Cita - ${sourceOption.label}` : (sourceOption.source as Service).name;
    return serviceIds.flatMap((serviceId): SuggestedItem[] => {
      const service = services.find((item) => item.id === serviceId) ?? (!appointment ? sourceOption.source as Service : undefined);
      const price = appointment?.servicePriceLocks?.[serviceId] ?? pricesByServiceId[serviceId];
      if (!service || !price) return [];
      return [{
        label: service.name,
        description: service.description?.trim() || defaultPaymentDescription(service.name),
        quantity: 1,
        amountCents: price.amountCents,
        sourceType: 'service',
        revenueType: 'service',
        sourceId: appointment?.id ?? service.id,
        customerId: appointment?.customerId,
        groupId: appointment?.id ?? service.id,
        groupLabel,
        parentId: appointment?.id ?? service.id,
        itemKind: 'service',
        paidCents: appointment ? paidCentsForAppointmentService(payments, appointment.id, serviceId) : 0,
        appointmentId: appointment?.id,
        serviceId,
        relatedStayId: appointment?.sourceStayId,
      }];
    });
  }

  const source = sourceOption.source as Stay | Daycare;
  const groupLabel = sourceOption.sourceType === 'stay'
    ? `Hotel · ${dogNamesForSource(source, dogs)}`
    : `Guardería · ${dogNamesForSource(source, dogs)}`;
  const items: SuggestedItem[] = [{
    label: groupLabel,
    quantity: 1,
    amountCents: 0,
    sourceType: sourceOption.sourceType,
    revenueType: sourceOption.sourceType,
    sourceId: source.id,
    customerId: source.customerId,
    groupId: source.id,
    groupLabel,
    parentId: source.id,
    itemKind: 'main',
  }];

  const sourcePrices = servicePricesForSource(source, pricesByServiceId);
  const dogServiceEntries = serviceIdsByDogForSource(source);
  for (const [dogId, serviceIds] of dogServiceEntries) {
    const uniqueServiceIds = [...new Set(serviceIds)];
    const dogName = dogs.find((dog) => dog.id === dogId)?.name ?? dogId;
    if (uniqueServiceIds.length > 0) {
      items.push({
        label: dogName,
        quantity: 1,
        amountCents: 0,
        sourceType: sourceOption.sourceType,
        revenueType: sourceOption.sourceType,
        sourceId: source.id,
        customerId: source.customerId,
        groupId: source.id,
        groupLabel,
        parentId: source.id,
        itemKind: 'dog',
        dogId,
      });
    }
    for (const serviceId of uniqueServiceIds) {
      const service = services.find((item) => item.id === serviceId);
      const price = sourcePrices[serviceId];
      if (!price) {
        items.push({
          label: `${service?.name ?? serviceId} - sin precio configurado`,
          quantity: 1,
          amountCents: 0,
          sourceType: sourceOption.sourceType,
          revenueType: 'service',
          sourceId: source.id,
          customerId: source.customerId,
          groupId: source.id,
          groupLabel,
          parentId: source.id,
          itemKind: 'service',
          dogId,
        });
        continue;
      }
      const quantity = quantityForUnit(price.billingUnit, sourceOption.sourceType === 'daycare' ? hoursBetween((source as Daycare).startAt, (source as Daycare).endAt) : 1, sourceOption.sourceType === 'stay' ? nightsBetween((source as Stay).checkInDate, (source as Stay).checkOutDate) : 1);
      const rowTotalCents = price.amountCents * quantity;
      const linkedAppointment = linkedAppointmentForSource(appointments, sourceOption.sourceType, source.id, dogId, serviceId);
      const paidCents = Math.min(rowTotalCents, linkedAppointment ? paidCentsForAppointmentService(payments, linkedAppointment.id, serviceId) : 0);
      const appointmentPaid = Boolean(linkedAppointment && paidCents > 0);
      items.push({
        description: service?.description?.trim() || defaultPaymentDescription(service?.name ?? serviceId),
        label: `${service?.name ?? serviceId}${appointmentPaid ? ' - pagado en cita' : paidCents > 0 ? ' - pagado' : ''}`,
        quantity,
        amountCents: price.amountCents,
        sourceType: sourceOption.sourceType,
        revenueType: service && isOperationalRatePrice(service, price) && sourceOption.sourceType === 'daycare' ? sourceOption.sourceType : 'service',
        sourceId: source.id,
        customerId: source.customerId,
        groupId: source.id,
        groupLabel,
        parentId: source.id,
        itemKind: 'service',
        dogId,
        paidCents,
        appointmentId: linkedAppointment?.id,
        serviceId,
        relatedStayId: sourceOption.sourceType === 'stay' ? source.id : undefined,
        relatedDaycareId: sourceOption.sourceType === 'daycare' ? source.id : undefined,
      });
    }
  }

  if (items.length > 0) return items;
  return serviceIdsForSource(source).reduce<SuggestedItem[]>((fallbackItems, serviceId) => {
    const service = services.find((item) => item.id === serviceId);
    const price = sourcePrices[serviceId];
    if (!service || !price) return fallbackItems;
    fallbackItems.push({
      label: service.name,
      quantity: 1,
      amountCents: price.amountCents,
      sourceType: sourceOption.sourceType,
      revenueType: service && isOperationalRatePrice(service, price) && sourceOption.sourceType === 'daycare' ? sourceOption.sourceType : 'service',
      sourceId: source.id,
      customerId: source.customerId,
      groupId: source.id,
      groupLabel,
      parentId: source.id,
      itemKind: 'service',
    });
    return fallbackItems;
  }, []);
}

export function suggestedItemsForMembership(membership: MembershipPurchase): SuggestedItem[] {
  return [{
    label: `Membresia · ${membership.packageName}`,
    quantity: 1,
    amountCents: membership.priceCents,
    sourceType: 'membership',
    revenueType: 'membership',
    sourceId: membership.id,
    customerId: membership.customerId,
    groupId: membership.id,
    groupLabel: `Membresia · ${membership.packageName}`,
    parentId: membership.id,
    itemKind: 'main',
  }];
}

export function serviceItemsForGroup(
  groupId: string,
  currentItems: CartItem[],
  stays: Stay[],
  daycares: Daycare[],
  linkedStay: Stay | undefined,
  linkedDaycare: Daycare | undefined,
  services: Service[],
  dogs: Dog[],
  pricesByServiceId: Record<string, Price>,
  priceLookups: ReturnType<typeof buildPriceLookups>,
  payments: Payment[],
  appointments: Appointment[],
): CartItem[] {
  const existingKeys = new Set(currentItems.map(serviceItemKey));
  return currentItems
    .filter((item) => item.groupId === groupId && item.itemKind === 'main' && (item.sourceType === 'stay' || item.sourceType === 'daycare'))
    .flatMap((item) => {
      const source = item.sourceType === 'stay'
        ? findSourceById(stays, linkedStay, item.parentId)
        : findSourceById(daycares, linkedDaycare, item.parentId);
      if (!source) return [];

      const suggested = suggestedItemsForSource(
        { id: source.id, label: item.label, sourceType: item.sourceType, source },
        services,
        dogs,
        pricesByServiceId,
        priceLookups,
        payments,
        appointments,
      );

      return suggested
        .filter((suggestion) => suggestion.itemKind === 'dog' || suggestion.itemKind === 'service')
        .map((suggestion): CartItem => ({ ...suggestion, id: crypto.randomUUID() }))
        .filter((serviceItem) => !existingKeys.has(serviceItemKey(serviceItem)));
    });
}

function findSourceById<T extends { readonly id: string }>(sources: readonly T[], linkedSource: T | undefined, id: string) {
  return sources.find((source) => source.id === id) ?? (linkedSource?.id === id ? linkedSource : undefined);
}

export function serviceItemKey(item: Pick<CartItem, 'dogId' | 'itemKind' | 'label' | 'parentId' | 'sourceId' | 'sourceType'>) {
  return `${item.itemKind}:${item.parentId}:${item.sourceType}:${item.sourceId}:${item.dogId ?? ''}:${item.label}`;
}

export function mergeServiceItemsForGroup(currentItems: CartItem[], groupId: string, serviceItems: CartItem[]): CartItem[] {
  const existingKeys = new Set(currentItems.map(serviceItemKey));
  const nextItems: CartItem[] = [];
  for (const item of currentItems) {
    nextItems.push(item);
    if (item.groupId !== groupId || item.itemKind !== 'main') continue;

    const missingForParent = serviceItems.filter((serviceItem) => serviceItem.parentId === item.parentId && !existingKeys.has(serviceItemKey(serviceItem)));
    nextItems.push(...missingForParent);
    for (const serviceItem of missingForParent) existingKeys.add(serviceItemKey(serviceItem));
  }
  return nextItems;
}

export function totalForSuggestedItems(items: SuggestedItem[]) {
  return items.reduce((total, item) => total + item.amountCents * item.quantity, 0);
}

export function paidCentsForSuggestedItems(items: readonly Pick<SuggestedItem, 'paidCents'>[]) {
  return items.reduce((total, item) => total + (item.paidCents ?? 0), 0);
}

export function paidCentsForAppointmentService(payments: Payment[], appointmentId: string, serviceId: string) {
  return payments.reduce((total, payment) => {
    const linkedItems = payment.items.filter((item) => item.sourceType === 'service' && item.appointmentId === appointmentId && item.serviceId === serviceId);
    const linkedTotal = linkedItems.reduce((sum, item) => sum + item.amountCents * item.quantity, 0);
    if (linkedTotal > 0) return total + linkedTotal;
    const directItems = payment.items.filter((item) => item.sourceType === 'service' && item.sourceId === appointmentId && (!item.serviceId || item.serviceId === serviceId));
    const directTotal = directItems.reduce((sum, item) => sum + item.amountCents * item.quantity, 0);
    if (directTotal > 0) return total + directTotal;
    return payment.sourceType === 'service' && payment.sourceId === appointmentId ? total + payment.totalCents : total;
  }, 0);
}

export function remainingForSource(sourceType: Exclude<SourceType, 'service'>, sourceId: string, suggestedItems: SuggestedItem[], payments: Payment[]) {
  return Math.max(0, totalForSuggestedItems(suggestedItems) - directlyPaidCentsForSource(payments, sourceType, sourceId));
}

export function isSourceOpenForPayment(sourceType: Exclude<SourceType, 'service'>, sourceId: string, suggestedItems: SuggestedItem[], payments: Payment[]) {
  const dueCents = totalWithFrozenFloor(payments, sourceType, sourceId, suggestedItems);
  if (dueCents === 0) return true;
  return directlyPaidCentsForSource(payments, sourceType, sourceId) < dueCents;
}

export function totalWithFrozenFloor(payments: Payment[], sourceType: Exclude<SourceType, 'service'>, sourceId: string, suggestedItems: SuggestedItem[]) {
  return Math.max(frozenBillTotalCentsForSource(payments, sourceType, sourceId) ?? 0, totalForSuggestedItems(suggestedItems));
}

export function paidCentsForSource(payments: Payment[], sourceType: Exclude<SourceType, 'service'>, sourceId: string) {
  return payments.reduce((total, payment) => {
    const itemTotal = payment.items
      .filter((item) =>
        (item.sourceType === sourceType && item.sourceId === sourceId) ||
        (sourceType === 'stay' && item.relatedStayId === sourceId) ||
        (sourceType === 'daycare' && item.relatedDaycareId === sourceId),
      )
      .reduce((itemSum, item) => itemSum + item.amountCents * item.quantity, 0);
    if (itemTotal > 0) return total + itemTotal;
    return payment.sourceType === sourceType && payment.sourceId === sourceId ? total + payment.totalCents : total;
  }, 0);
}

function directlyPaidCentsForSource(payments: Payment[], sourceType: Exclude<SourceType, 'service'>, sourceId: string) {
  return payments.reduce((total, payment) => {
    const itemTotal = payment.items
      .filter((item) => item.sourceType === sourceType && item.sourceId === sourceId)
      .reduce((itemSum, item) => itemSum + item.amountCents * item.quantity, 0);
    if (itemTotal > 0) return total + itemTotal;
    return payment.sourceType === sourceType && payment.sourceId === sourceId ? total + payment.totalCents : total;
  }, 0);
}

export function frozenBillTotalCentsForSource(payments: Payment[], sourceType: SourceType, sourceId: string) {
  if (sourceType === 'service') return undefined;
  return payments.reduce<number | undefined>((current, payment) => {
    const matchingItem = payment.items.find((item) => item.sourceType === sourceType && item.sourceId === sourceId && typeof item.billTotalCents === 'number');
    const nextTotal =
      matchingItem?.billTotalCents ??
      (payment.sourceType === sourceType && payment.sourceId === sourceId ? payment.billTotalCents : undefined);
    if (typeof nextTotal !== 'number') return current;
    return typeof current === 'number' ? Math.max(current, nextTotal) : nextTotal;
  }, undefined);
}

export function billReferenceForSource(payments: Payment[], sourceType: SourceType, sourceId: string) {
  const directPayment = payments.find((payment) => payment.sourceType === sourceType && payment.sourceId === sourceId && payment.billReference);
  const itemPayment = payments.find((payment) => payment.items.some((item) => item.sourceType === sourceType && item.sourceId === sourceId && item.billReference));
  return directPayment?.billReference ?? itemPayment?.items.find((item) => item.sourceType === sourceType && item.sourceId === sourceId)?.billReference ?? createBillReference(sourceType, sourceId);
}

export function createBillReference(sourceType: SourceType, sourceId: string) {
  const prefix = sourceType === 'other' ? 'OTR' : sourceType === 'stay' ? 'EST' : sourceType === 'daycare' ? 'GUA' : sourceType === 'membership' ? 'MEM' : 'SER';
  return `${prefix}-${sourceId.replace(/[^a-z0-9]/gi, '').slice(-6).toUpperCase()}`;
}

export function buildPaymentLineItems(items: CartItem[], payments: Payment[], paymentCents: number): PaymentCreate['items'] {
  const remainingCartCents = paymentBuckets(items, payments).reduce((total, bucket) => total + bucket.remainingCents, 0);
  let remainingToAllocate = Math.min(paymentCents, remainingCartCents);
  const lineItems: PaymentCreate['items'] = [];
  const bucketTotals = new Map(paymentBuckets(items, payments).map((bucket) => [sourceKey(bucket.sourceType, bucket.sourceId), bucket]));
  const payableItems = items.filter((item) => item.itemKind !== 'dog' && item.amountCents > 0);

  for (const item of payableItems) {
    if (remainingToAllocate <= 0) break;
    const itemRemainingCents = Math.max(0, item.amountCents * item.quantity - (item.paidCents ?? 0));
    const amountCents = Math.min(itemRemainingCents, remainingToAllocate);
    if (amountCents <= 0) continue;
    const bucket = bucketTotals.get(sourceKey(item.sourceType, item.sourceId));

    lineItems.push({
      label: `${amountCents < itemRemainingCents ? 'Pago parcial' : 'Pago'} - ${item.label}`,
      description: item.description ?? (item.sourceType === 'other' ? undefined : defaultPaymentDescription(item.label)),
      quantity: item.sourceType === 'other' && amountCents === item.amountCents * item.quantity ? item.quantity : 1,
      amountCents: item.sourceType === 'other' && amountCents === item.amountCents * item.quantity ? item.amountCents : amountCents,
      sourceType: item.sourceType,
      revenueType: item.revenueType,
      sourceId: item.sourceId,
      appointmentId: item.appointmentId,
      serviceId: item.serviceId,
      relatedStayId: item.relatedStayId,
      relatedDaycareId: item.relatedDaycareId,
      billReference: bucket?.billReference,
      billTotalCents: bucket?.dueCents,
    });
    remainingToAllocate -= amountCents;
  }

  return lineItems;
}

export function paymentBuckets(items: CartItem[], payments: Payment[]) {
  const buckets = new Map<string, { label: string; sourceType: SourceType; sourceId: string; billReference: string; itemTotalCents: number; dueCents: number; paidCents: number; remainingCents: number }>();
  for (const item of items) {
    const key = sourceKey(item.sourceType, item.sourceId);
    const existing = buckets.get(key);
    const itemTotal = item.amountCents * item.quantity;
    if (existing) {
      existing.itemTotalCents += itemTotal;
      existing.paidCents += item.paidCents ?? 0;
      existing.dueCents = Math.max(frozenBillTotalCentsForSource(payments, existing.sourceType, existing.sourceId) ?? 0, existing.itemTotalCents);
      existing.remainingCents = Math.max(0, existing.dueCents - existing.paidCents);
      if (item.itemKind === 'main') existing.label = item.label;
      continue;
    }

    const paidCents = (item.sourceType === 'service' ? 0 : directlyPaidCentsForSource(payments, item.sourceType, item.sourceId)) + (item.paidCents ?? 0);
    const frozenTotal = item.sourceType === 'service' ? undefined : frozenBillTotalCentsForSource(payments, item.sourceType, item.sourceId);
    const dueCents = Math.max(frozenTotal ?? 0, itemTotal);
    buckets.set(key, {
      label: item.itemKind === 'main' ? item.label : item.groupLabel,
      sourceType: item.sourceType,
      sourceId: item.sourceId,
      billReference: billReferenceForSource(payments, item.sourceType, item.sourceId),
      itemTotalCents: itemTotal,
      dueCents,
      paidCents,
      remainingCents: Math.max(0, dueCents - paidCents),
    });
  }

  return [...buckets.values()].map((bucket) => ({ ...bucket, remainingCents: Math.max(0, bucket.dueCents - bucket.paidCents) }));
}

export function sourceKey(sourceType: SourceType, sourceId: string) {
  return `${sourceType}:${sourceId}`;
}

export function groupCartItems(items: CartItem[]): CartGroup[] {
  const groups = new Map<string, CartGroup>();
  for (const item of items) {
    const existing = groups.get(item.groupId);
    if (existing) {
      existing.items.push(item);
      continue;
    }
    groups.set(item.groupId, { id: item.groupId, label: item.groupLabel, items: [item] });
  }
  return [...groups.values()];
}

export function dogNamesForSource(source: { dogId: string; dogIds?: readonly string[] }, dogs: Dog[]) {
  return dogIdsForSource(source).map((dogId) => dogs.find((dog) => dog.id === dogId)?.name ?? dogId).join(', ');
}

function linkedAppointmentForSource(appointments: Appointment[], sourceType: SourceType, sourceId: string, dogId: string, serviceId: string) {
  if (sourceType === 'stay') {
    return appointments.find((appointment) => appointment.sourceStayId === sourceId && appointment.dogIds.includes(dogId) && appointment.serviceIds.includes(serviceId));
  }
  if (sourceType === 'daycare') {
    return appointments.find((appointment) => appointment.sourceDaycareId === sourceId && appointment.dogIds.includes(dogId) && appointment.serviceIds.includes(serviceId));
  }
  return undefined;
}

function isAppointment(source: SourceOption['source']): source is Appointment {
  return 'scheduledAt' in source && 'serviceIds' in source;
}

export function defaultPaymentDescription(label: string) {
  return `Cobro por ${label.replace(/^Pago(?: parcial)? - /, '').trim()}.`.slice(0, 1000);
}

export function customCharge(input: { customerId: string; amount: string; quantity: string; description?: string }, createId: () => string): CartItem {
  const quantity = Number(input.quantity);
  const amount = Number(input.amount);
  const amountCents = Math.round(amount * 100);
  if (!input.customerId) throw new Error('Selecciona un cliente.');
  if (!Number.isSafeInteger(quantity) || quantity <= 0) throw new Error('La cantidad debe ser un entero mayor a cero.');
  if (!Number.isFinite(amount) || amount <= 0 || !Number.isSafeInteger(amountCents) || amountCents <= 0 || !Number.isSafeInteger(amountCents * quantity)) throw new Error('El precio debe ser mayor a cero y tener un importe válido.');
  if (Math.abs(amount * 100 - amountCents) > 0.000001) throw new Error('El precio admite hasta dos decimales.');
  if ((input.description?.length ?? 0) > 1000) throw new Error('La descripción admite hasta 1000 caracteres.');
  const id = createId();
  return { id, label: 'Otro', description: input.description?.trim() || undefined, quantity, amountCents,
    sourceType: 'other', revenueType: 'other', sourceId: id, customerId: input.customerId,
    groupId: id, groupLabel: 'Otro', parentId: id, itemKind: 'service' };
}
